"use client"

import SearchBar from "./Header/SearchBar"
import Navigation from "./Header/NavigationMenu"
import MobileNavigation from "./Mobile/MobileNavigation"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, Phone, Mail, Clock, MapPin } from "lucide-react"
import { useState, useEffect } from "react"
import { useCompanyInfo } from "@/shared/hooks/useCompanyInfo"


// Throttle function to limit scroll event frequency
const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

const Header = () => {
  const { getByKey } = useCompanyInfo()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState<boolean>(false)
  const location = useLocation()

  // Company contact info
  const email = getByKey('email_primary') || 'info@twi.com.ph'
  const phone = getByKey('phone_mobile') || '(+63) 985 590 9000'
  const businessHours = getByKey('business_hours_weekday') || 'Mon-Fri: 8:30AM-5:30PM'
  const address = getByKey('address_city') || 'Pasig City, Philippines'

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev)

  const isWhiteBackgroundPage = [
    "/about",
    "/contact",
    "/visa",
    "/fly-and-share",
    "/privacy-policy",
    "/terms-of-service"
  ].includes(location.pathname)
  const showOverhang = isScrolled || isWhiteBackgroundPage

  useEffect(() => {
    const handleScroll = (): void => {
      const scrollThreshold = 100
      const currentScroll = window.scrollY
      
      if (currentScroll > scrollThreshold) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    const throttledHandleScroll = throttle(handleScroll, 16) // ~60fps

    handleScroll() // Check on mount
    window.addEventListener("scroll", throttledHandleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll)
    }
  }, [])

  return (
    <>
      {/* Modern Top Info Bar - Only show when scrolled */}
      {showOverhang && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white border-b border-slate-700/50 animate-in slide-in-from-top duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-2 text-sm">
              <div className="hidden md:flex items-center space-x-6">
                <div className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-300">
                  <div className="w-4 h-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <Phone className="w-2 h-2 text-white" />
                  </div>
                  <span className="font-medium">{phone}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-300">
                  <div className="w-4 h-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                    <Mail className="w-2 h-2 text-white" />
                  </div>
                  <span className="font-medium">{email}</span>
                </div>
              </div>
              
              <div className="hidden lg:flex items-center space-x-6 text-slate-300">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                    <Clock className="w-2 h-2 text-white" />
                  </div>
                  <span className="font-medium">{businessHours}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                    <MapPin className="w-2 h-2 text-white" />
                  </div>
                  <span className="font-medium">{address}</span>
                </div>
              </div>
              
              {/* Mobile - Show only phone and email */}
              <div className="flex md:hidden items-center space-x-4 text-xs">
                <div className="flex items-center gap-1.5 text-slate-300">
                  <Phone className="w-3 h-3" />
                  <span>{phone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <header
        className={`fixed ${showOverhang ? 'top-[40px]' : 'top-0'} right-0 left-0 z-50 w-full h-[90px] min-h-[90px] shrink-0 flex items-center transition-all duration-300 header-slide-down ${
          showOverhang
            ? "bg-white/95 text-black shadow-lg backdrop-blur-lg border-b border-gray-100"
            : "bg-transparent text-white shadow-none"
        }`}
      >
        <div className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4 sm:gap-6 lg:gap-8">
            {/* Logo */}
            <Link 
              to="/" 
              aria-label="Go to home"
              className="flex items-center space-x-2 transition-smooth hover:opacity-80 logo-hover shrink-0"
            >
              <img 
                src="/logo.svg" 
                alt="Travel Warehouse, Inc." 
                className="h-20 w-32 sm:h-24 sm:w-40 object-contain"
                onError={(e) => {
                  // Fallback if logo doesn't exist
                  e.currentTarget.style.display = 'none'
                  e.currentTarget.nextElementSibling?.classList?.remove('hidden')
                }}
              />
              <div className="hidden text-lg font-bold text-amber-600">
                Travel Warehouse
              </div>
            </Link>

            {/* Main navigation */}
            <nav className="hidden lg:flex space-x-6 xl:space-x-8 justify-left flex-1 items-center">
              <Navigation isWhiteBackground={showOverhang} />
            </nav>

            {/* Search and CTA - Hidden on small screens */}
            <div className="hidden md:flex">
              <SearchBar />
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`rounded-lg p-2.5 transition-smooth md:hidden shadow-sm hover:shadow-md mobile-menu-btn ${
                isScrolled || isWhiteBackgroundPage 
                  ? isMobileMenuOpen
                    ? "bg-amber-600 text-white hover:bg-rust-700 scale-105"
                    : "hover:bg-gray-100 text-gray-700 border border-gray-300 hover:border-gray-400"
                  : isMobileMenuOpen
                    ? "bg-amber-600 text-white hover:bg-rust-700 scale-105"
                    : "hover:bg-white/20 text-white border border-white/40 hover:border-white/60 backdrop-blur-sm"
              } ${isMobileMenuOpen ? '!bg-amber-600 !text-white' : ''}`}
              style={isMobileMenuOpen ? { background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)', color: 'white' } : {}}
              onMouseEnter={(e) => {
                if (isMobileMenuOpen) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)'
                  e.currentTarget.style.color = 'white'
                }
              }}
              onMouseLeave={(e) => {
                if (isMobileMenuOpen) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)'
                  e.currentTarget.style.color = 'white'
                }
              }}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5 transition-smooth rotate-90" />
              ) : (
                <Menu className="h-5 w-5 transition-smooth" />
              )}
            </button>
          </div>
        </div>
      </div>
      <MobileNavigation isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </header>
    </>
  )
}

export default Header
